import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Design System
import { Toggle } from 'jl-design-system/components/toggle/Toggle';
import Tooltip from 'jl-design-system/components/tooltip/Tooltip';
import { Message } from 'jl-design-system/components/message/Message';

// Types
import { AppDispatch } from 'types/RootState.types';

// Config
import rewardsState from './Rewards.state';
import { ANALYTICS_REWARDS_TOOLTIP, ANALYTICS_REWARDS_TOGGLE } from '@constants/actionConstants';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { applyRewards, removeRewards } from '@redux/actions/payment/rewardsActions';

// Components
import Container from '@components/container';
import RewardsPromoWarning from '@components/rewards-promo-warning/RewardsPromoWarning';

// Styles
import styles from './rewards.scss';

const Rewards = () => {
  const dispatch: AppDispatch = useDispatch();

  const {
    disabled,
    showErrorMessage,
    rewardApplicable,
    rewardsDiscountTotal,
    rewards = [],
    shouldShowWarningOnApply,
  } = useSelector(rewardsState);

  const [showWarningModal, setShowWarningModal] = useState(false);

  if (!rewardApplicable) return null;

  if (!rewards.length) return null;

  const reward = rewards[0];

  const onChange = (evt: any) => {
    const isChecked = evt.target.checked;

    if (isChecked) {
      if (shouldShowWarningOnApply) {
        setShowWarningModal(true);
      } else dispatch(applyRewards({ shouldGetPayments: true, isCustomerAction: true }));
    } else if (reward?.id) dispatch(removeRewards(reward.id));

    dispatch(triggerAnalyticsEvent(ANALYTICS_REWARDS_TOGGLE, { isChecked }));
  };

  const onClose = () => setShowWarningModal(false);

  const onTooltipOpen = () => dispatch(triggerAnalyticsEvent(ANALYTICS_REWARDS_TOOLTIP));

  const rewardIsActive = !!rewardsDiscountTotal;

  return (
    <Container background="white" paddingTop="2" paddingX="3">
      <Container marginBottom="2" renderIf={showErrorMessage}>
        <Message
          body="Please try again. If the problem persists, please try again later."
          title="Sorry, something went wrong"
          type="error"
        />
      </Container>

      <div className={styles.toggle} data-testid="reward-toggle">
        <Toggle
          checked={rewardIsActive}
          className={disabled ? styles.disabled : null}
          disabled={disabled}
          label="Apply reward"
          labelTextPosition="right"
          name="rewards"
          onChange={onChange}
          updatedStyling
        />
        {rewardIsActive && reward?.campaignFriendlyName && (
          <Tooltip icon id="reward-description" onOpen={onTooltipOpen}>
            {reward.campaignFriendlyName}
          </Tooltip>
        )}
      </div>
      {showWarningModal && (
        <RewardsPromoWarning
          direction="reward-to-promo"
          onCancel={onClose}
          onContinue={() => {
            dispatch(applyRewards({ shouldGetPayments: true, isCustomerAction: true }));
            onClose();
          }}
        />
      )}
    </Container>
  );
};

export default Rewards;
